import type { FaqEntry } from '../faq';

/**
 * French FAQ, translated from `es.ts`, which is the source.
 *
 * Product names follow the catalogs: `Redondeo Solidario` is "Arrondi Solidaire" and
 * `entidades sociales` is "entités sociales". Formal second person ("vous"), which is what the rest of
 * the French catalog uses — French is published on the redesign only, and this page must not be the
 * one that switches register.
 */
export const faqEntriesFr: FaqEntry[] = [
	// MARK: 1. Pour les entreprises
	{
		block: 'companies',
		question: 'Qu’est-ce que Worldcoo ?',
		answer:
			'Worldcoo est une entreprise technologique qui met en relation entreprises, particuliers et entités sociales pour faciliter de nouvelles formes de don et générer un impact social et environnemental. Depuis 2012, nous développons des solutions qui intègrent le don dans les canaux d’achat des entreprises et transforment de petits gestes du quotidien en occasions d’agir.',
	},
	{
		block: 'companies',
		question: 'Quelles solutions Worldcoo propose-t-il ?',
		answer:
			'Notre objectif est de faciliter l’intégration de la solidarité dans les différents points de contact entre les entreprises et leurs clients ou leurs collaborateurs. Worldcoo développe plusieurs solutions de don qui s’adaptent aux canaux et aux besoins de chaque entreprise. Parmi elles : l’Arrondi Solidaire, les dons en e-commerce, les portails de don et les solutions de don liées à la paie ou aux factures.',
	},
	{
		block: 'companies',
		question: 'Qu’est-ce que l’Arrondi Solidaire ?',
		answer:
			'L’Arrondi Solidaire permet aux clients d’une entreprise de faire un petit don au moment de finaliser un achat. Le client peut arrondir le montant de son achat et donner la différence à un projet social ou environnemental sélectionné.',
	},
	{
		block: 'companies',
		question: 'Combien coûte l’intégration de Worldcoo ?',
		answer:
			'Nous étudions chaque cas de façon personnalisée, en tenant compte des besoins de l’entreprise, de la solution qu’elle souhaite mettre en place et du type d’intégration technique nécessaire. Selon l’opérationnel et l’implémentation requis, l’intégration des solutions de Worldcoo peut être gratuite.',
	},
	{
		block: 'companies',
		question: 'Dois-je changer mon système de paiement pour intégrer Worldcoo ?',
		answer:
			'Worldcoo travaille avec différents prestataires technologiques et systèmes de paiement pour intégrer ses solutions dans les canaux de chaque entreprise. Notre équipe étudie chaque cas afin d’identifier la meilleure option d’intégration.',
	},
	{
		block: 'companies',
		question: 'Sur quels canaux puis-je activer Worldcoo ?',
		answer:
			'Les solutions de Worldcoo peuvent s’intégrer à différents points de contact avec le client, physiques comme numériques, ainsi qu’avec les collaborateurs :',
		bullets: [
			'Magasins et points de vente physiques.',
			'E-commerce.',
			'Applications.',
			'Portails de don en ligne',
			'Paie et factures',
		],
		outro: 'La solution s’adapte aux caractéristiques et aux besoins de chaque entreprise.',
	},
	{
		block: 'companies',
		question: 'Puis-je choisir le montant du don du client ?',
		answer:
			'Oui. Nous travaillons avec chaque entreprise pour identifier des projets sociaux ou environnementaux alignés sur ses valeurs, ses objectifs et ses priorités. Worldcoo propose une sélection de projets susceptibles de correspondre à chaque société, mais la décision finale sur la cause à soutenir revient toujours à l’entreprise.',
	},
	{
		block: 'companies',
		question: 'Puis-je choisir la cause ou le projet que je souhaite soutenir ?',
		answer:
			'Oui. Worldcoo travaille avec les entreprises pour identifier des projets alignés sur leurs valeurs, leurs objectifs d’impact et leurs priorités sociales ou environnementales. Worldcoo présente une proposition de projets qui lui semblent correspondre à chaque société en particulier, mais c’est l’entreprise qui a le dernier mot sur le projet retenu.',
	},
	{
		block: 'companies',
		question: 'Puis-je soutenir des projets locaux ?',
		answer:
			'Oui. Chez Worldcoo, nous travaillons avec des entités sociales de tailles et de champs d’action variés, y compris des organisations locales qui mènent des projets à fort impact dans leurs communautés. L’un de nos objectifs est de faciliter aussi l’accès au financement pour ces entités et de donner de la visibilité au travail essentiel qu’elles accomplissent.',
	},
	{
		block: 'companies',
		question: 'Puis-je changer de projet ?',
		answer:
			'Oui. Les campagnes peuvent être mises à jour lorsqu’un projet atteint son objectif de financement, lorsqu’il se termine, ou lorsque l’entreprise décide de soutenir une nouvelle cause.',
	},
	{
		block: 'companies',
		question: 'Puis-je savoir combien a été collecté ?',
		answer:
			'Oui. Worldcoo met à disposition des outils de suivi qui permettent de connaître l’évolution des campagnes et l’impact généré, au jour le jour et par point de vente.',
	},
	{
		block: 'companies',
		question: 'Puis-je communiquer les résultats de la campagne ?',
		answer:
			'Oui. Worldcoo fournit des informations sur l’évolution des campagnes et sur les projets financés afin de faciliter la communication de l’impact généré.',
	},

	// MARK: 2. Pour les entités sociales
	{
		block: 'ngos',
		question: 'Comment mon entité peut-elle collecter des fonds via Worldcoo ?',
		answer:
			'Les entités sociales peuvent présenter leurs projets à Worldcoo pour être retenues dans les campagnes de collecte lancées avec des entreprises partenaires. Notre équipe étudie chaque proposition et, lorsqu’une opportunité de collaboration se présente, travaille avec l’entité pour lancer la campagne.',
	},
	{
		block: 'ngos',
		question: 'Quels types de projets Worldcoo finance-t-il ?',
		answer: 'Nous travaillons avec des projets relevant de différents domaines sociaux et environnementaux, dont :',
		bullets: [
			'Enfance et jeunesse.',
			'Inclusion sociale.',
			'Santé.',
			'Personnes âgées.',
			'Handicap.',
			'Environnement.',
			'Coopération.',
			'Urgences.',
			'Autres domaines d’impact social.',
		],
	},
	{
		block: 'ngos',
		question: 'Quelles conditions une entité doit-elle remplir pour collaborer avec Worldcoo ?',
		answer:
			'Les entités doivent respecter les critères de sélection définis par Worldcoo et fournir les documents nécessaires pour justifier de leur constitution, de leur activité et de leur situation juridique. Elles doivent également être en mesure d’émettre les reçus fiscaux correspondants.',
		outro: 'Vous pouvez consulter les critères de sélection de Worldcoo pour connaître toutes les conditions.',
		link: { label: 'Critères de sélection', target: 'selection-criteria' },
	},
	{
		block: 'ngos',
		question: 'Comment commencer à collaborer ?',
		answer:
			'Vous pouvez lancer la démarche en remplissant le formulaire d’inscription avec les informations de base de votre entité.',
		link: { label: 'Accéder au formulaire d’inscription', target: 'ngo-signup' },
	},
	{
		block: 'ngos',
		question: 'Puis-je présenter un projet précis ?',
		answer:
			'Oui. Worldcoo aide à financer des projets précis portés par les entités sociales, et non les entités de façon générale. Vous pouvez nous présenter un ou plusieurs projets ayant besoin de financement. Notre équipe évaluera leur adéquation à nos critères de collaboration et cherchera les opportunités qui correspondent le mieux à chaque projet.',
	},
	{
		block: 'ngos',
		question: 'Les petites entités ou les entités locales peuvent-elles collaborer ?',
		answer:
			'Oui. Chez Worldcoo, nous voulons contribuer à ce que les entités locales et de plus petite taille accèdent elles aussi à de nouvelles opportunités de financement. Beaucoup de ces organisations mènent un travail essentiel dans leurs communautés et génèrent un impact direct très important.',
	},
	{
		block: 'ngos',
		question: 'Comment fonctionne le processus de sélection des projets ?',
		answer:
			'Une fois que vous faites partie de la communauté Worldcoo, vos projets rejoignent notre base de données. Lorsqu’une opportunité de collaboration se présente, nous proposons les projets qui correspondent le mieux aux besoins et aux critères de chaque entreprise, en tenant compte d’éléments comme le périmètre géographique et thématique, sa stratégie RSE ou le profil de ses clients.',
		outro:
			'La décision finale sur l’organisation retenue pour chaque campagne revient toujours à l’entreprise partenaire.',
	},
	{
		block: 'ngos',
		question: 'Une fois un projet financé, comment les fonds collectés sont-ils gérés ?',
		answer:
			'À la fin de la campagne de collecte, Worldcoo gère le transfert de 100 % des fonds à l’entité sociale bénéficiaire. Le processus se déroule en deux versements :',
		bullets: [
			'Un premier versement de 50 % du montant collecté à la fin de la campagne.',
			'Un second versement des 50 % restants une fois que l’entité a remis le rapport de suivi et de justification du projet.',
		],
	},
	{
		block: 'ngos',
		question: 'À quoi une entité s’engage-t-elle lorsqu’elle reçoit un financement ?',
		answer:
			'Les entités qui reçoivent des fonds via Worldcoo s’engagent à rendre compte de la mise en œuvre et de l’impact du projet financé. Pour cela, elles doivent remettre un rapport comprenant des informations sur :',
		bullets: [
			'Le déroulement du projet et les personnes bénéficiaires.',
			'Les résultats et l’impact obtenus.',
			'Des images ou d’autres documents permettant de suivre l’évolution du projet.',
			'L’utilisation et la destination des fonds reçus.',
		],
	},
	{
		block: 'ngos',
		question: 'Combien coûte une collaboration avec Worldcoo pour une entité sociale ?',
		answer:
			'Worldcoo propose aux entités sociales des services de collecte, de gestion et de suivi des fonds, ainsi que l’accès à de nouvelles opportunités de collaboration avec des entreprises. Pour ces services, Worldcoo applique des frais sur les fonds gérés. Les conditions sont communiquées en toute transparence à chaque entité avant le début de toute collaboration.',
	},

	// MARK: 3. Pour les donateurs
	{
		block: 'donors',
		question: 'Qu’est-ce que l’Arrondi Solidaire ?',
		answer:
			'L’Arrondi Solidaire est une façon simple de faire un petit don au moment d’un achat. Au paiement, vous pouvez arrondir le montant de votre achat et destiner ces centimes à un projet social ou environnemental.',
	},
	{
		block: 'donors',
		question: 'Où puis-je faire un don ?',
		answer:
			'Vous trouverez les solutions de don de Worldcoo dans différents points de vente et canaux numériques de nos entreprises partenaires.',
	},
	{
		block: 'donors',
		question: 'Quel montant est-ce que je donne ?',
		answer:
			'Le montant du don dépend de la solution et de la campagne à laquelle vous participez. Avec l’Arrondi Solidaire, vous pouvez arrondir le montant de votre achat et donner la différence. Dans d’autres campagnes, vous pouvez faire un don d’un montant fixe défini à l’avance. Avant de confirmer votre don, vous pourrez toujours connaître le montant que vous allez verser.',
	},
	{
		block: 'donors',
		question: 'Puis-je décider si je souhaite donner ?',
		answer:
			'Oui. La participation à l’Arrondi Solidaire est volontaire et anonyme. Avant de finaliser le don, le système vous indique le montant que vous pouvez verser.',
	},
	{
		block: 'donors',
		question: 'À quel projet mon don est-il destiné ?',
		answer:
			'Votre don est destiné au projet associé à la campagne à laquelle vous participez. Vous pouvez consulter des informations sur le projet, l’entité responsable et l’évolution de la campagne via les canaux mis en place par Worldcoo et par chaque entreprise.',
	},
	{
		block: 'donors',
		question: 'Comment puis-je savoir combien a été collecté ?',
		answer:
			'Worldcoo publie des informations sur les objectifs de chaque campagne et sur son évolution, afin de faciliter le suivi des dons et de l’impact généré.',
	},
	{
		block: 'donors',
		question: 'Que devient mon argent après un don ?',
		answer:
			'Une fois le don effectué, Worldcoo gère et suit les fonds collectés pendant la campagne. À la fin de la campagne, les fonds sont versés à l’entité sociale responsable du projet pour contribuer à son financement. L’entité rend ensuite compte du déroulement du projet et de l’impact obtenu grâce aux fonds collectés.',
	},
	{
		block: 'donors',
		question: 'Puis-je obtenir un reçu pour mon don ?',
		answer:
			'Oui. Vous pouvez demander votre reçu de don sur app.worldcoo.com. Pour cela, vous devrez conserver le ticket ou le justificatif de votre don, sur lequel figure le code de don qui permet de l’identifier.',
	},
	{
		block: 'donors',
		question: 'Qu’est-ce que le code de don ?',
		answer:
			'Le code de don est un identifiant unique associé à votre contribution. Vous le trouverez sur le ticket ou le justificatif de don et pourrez l’utiliser pour enregistrer votre don sur app.worldcoo.com et demander, le cas échéant, votre reçu de don.',
	},
	{
		block: 'donors',
		question: 'Comment connaître tous les dons que j’ai faits ?',
		answer:
			'Sur app.worldcoo.com, vous pouvez enregistrer et consulter les dons que vous avez faits. La plateforme vous permet de conserver un historique de vos dons et de demander les reçus correspondants.',
	},
	{
		block: 'donors',
		question: 'Puis-je annuler un don ou en demander le remboursement ?',
		answer:
			'Oui. Vous pouvez demander le remboursement d’un don. L’argent donné est géré directement par Worldcoo, qui se chargera donc du remboursement du montant versé à la demande du client.',
		outro: 'Rendez-vous dans notre rubrique contact et écrivez-nous en nous expliquant votre cas.',
		link: { label: 'Contactez-nous', target: 'contact-donor' },
	},

	// MARK: 4. Transparence et sécurité
	{
		block: 'transparency',
		question: 'Comment Worldcoo garantit-il la transparence des dons ?',
		answer:
			'La transparence est l’un des piliers de notre modèle. Worldcoo assure le suivi des campagnes et publie des informations sur les montants collectés, les projets financés et les entités participantes. Nous publions par ailleurs régulièrement des informations sur notre activité et sur l’impact généré.',
	},
	{
		block: 'transparency',
		question: 'Quelle part de l’argent parvient aux entités sociales ?',
		answer:
			'100 % des dons collectés lors de chaque campagne sont destinés au projet et à l’entité sociale chargée de le mener à bien. L’ONG effectue ensuite un paiement à Worldcoo pour les services rendus.',
	},
	{
		block: 'transparency',
		question: 'Combien de projets Worldcoo a-t-il financés ?',
		answer:
			'Depuis notre création, Worldcoo a contribué au financement de plus de 1 000 projets sociaux et environnementaux, en collaboration avec des centaines d’entités.',
	},
	{
		block: 'transparency',
		question: 'Combien de dons ont été réalisés via Worldcoo ?',
		answer:
			'Des millions de personnes ont participé à des campagnes Worldcoo et plus de 100 millions de microdons ont été effectués.',
	},
	{
		block: 'transparency',
		question: 'Où puis-je consulter l’impact de Worldcoo ?',
		answer:
			'Vous pouvez consulter des informations sur les campagnes, les projets et l’impact généré par Worldcoo sur notre site. Nous publions également de façon régulière des informations sur notre activité et notre rapport d’impact.',
	},
	{
		block: 'transparency',
		question: 'Est-il sûr de donner via Worldcoo ?',
		answer:
			'Oui. Les solutions de Worldcoo sont intégrées aux systèmes de paiement de nos entreprises partenaires et sont développées selon les standards de sécurité applicables. Worldcoo assure en outre le suivi des campagnes et des fonds collectés afin de garantir une gestion transparente des dons.',
	},
	{
		block: 'transparency',
		question: 'Worldcoo conserve-t-il les données de ma carte ?',
		answer: 'Worldcoo n’a pas besoin de conserver les données complètes de votre carte pour que vous puissiez donner.',
	},
	{
		block: 'transparency',
		question: 'Comment mes données personnelles sont-elles utilisées ?',
		answer:
			'Les données personnelles sont traitées conformément à notre Politique de confidentialité et à la réglementation en vigueur en matière de protection des données.',
	},

	// MARK: 5. À propos de Worldcoo
	{
		block: 'about',
		question: 'Quand Worldcoo a-t-il été créé ?',
		answer:
			'Worldcoo est né en 2012 avec l’objectif d’utiliser la technologie pour faciliter le financement de projets sociaux et environnementaux.',
	},
	{
		block: 'about',
		question: 'Où Worldcoo opère-t-il ?',
		answer:
			'Worldcoo travaille avec des entreprises et des entités sociales sur différents marchés et oriente les fonds vers des projets tant nationaux qu’internationaux.',
	},
	{
		block: 'about',
		question: 'Worldcoo est-il une ONG ?',
		answer:
			'Non. Worldcoo est une entreprise technologique spécialisée dans les solutions de microdon. Notre objectif est de mettre en relation entreprises, particuliers et entités sociales pour faciliter la collaboration et générer de l’impact.',
	},
	{
		block: 'about',
		question: 'Worldcoo est-il une entreprise B Corp ?',
		answer:
			'Oui. Worldcoo est certifié B Corp, une certification qui reconnaît l’engagement des entreprises envers des standards élevés d’impact social et environnemental, de transparence et de responsabilité.',
	},
	{
		block: 'about',
		question: 'Pourquoi Worldcoo ?',
		answer:
			'Parce que nous pensons que donner devrait être simple. Chez Worldcoo, nous utilisons la technologie pour mettre en relation entreprises, particuliers et entités sociales, et transformer des millions de petits gestes du quotidien en occasions d’agir. Depuis 2012, nous travaillons à rendre la collaboration sociale toujours plus simple, accessible et transparente.',
	},
];
